/**
 * ISMS (Information Security Management System) Page
 * EASA Part-IS Compliance Module
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Shield, AlertTriangle, Server, CheckCircle, Clock, Plus,
  TrendingUp, FileWarning, Lock, Eye, Edit, Trash2, RefreshCw,
  ChevronRight, Target, AlertCircle, Activity, Calendar,
  ShieldCheck, ShieldAlert, Database, Network, Users, Building2,
  FileText, Search, Filter, MoreVertical, Bell, BellRing, X,
  ExternalLink, Info, Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Alert type colors
const ALERT_COLORS = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500', text: 'text-red-600', icon: AlertCircle },
  warning: { bg: 'bg-orange-500/10', border: 'border-orange-500', text: 'text-orange-600', icon: AlertTriangle },
  info: { bg: 'bg-blue-500/10', border: 'border-blue-500', text: 'text-blue-600', icon: Info }
};

// Risk level colors and labels
const RISK_LEVELS = {
  critical: { color: 'bg-red-500', text: 'text-red-500', label: 'Critical' },
  high: { color: 'bg-orange-500', text: 'text-orange-500', label: 'High' },
  medium: { color: 'bg-yellow-500', text: 'text-yellow-500', label: 'Medium' },
  low: { color: 'bg-green-500', text: 'text-green-500', label: 'Low' }
};

// PSOE Level progression
const PSOE_LEVELS = {
  not_started: { color: 'bg-gray-400', label: 'Not Started', value: 0 },
  present: { color: 'bg-blue-400', label: 'Present', value: 25 },
  suitable: { color: 'bg-yellow-400', label: 'Suitable', value: 50 },
  operational: { color: 'bg-orange-400', label: 'Operational', value: 75 },
  effective: { color: 'bg-green-500', label: 'Effective', value: 100 }
};

// Incident severity colors
const SEVERITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500'
};

// Asset type icons
const ASSET_ICONS = {
  hardware: Server,
  software: Database,
  data: FileText,
  network: Network,
  personnel: Users,
  facility: Building2,
  service: Activity
};

export default function ISMSPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  
  // Alerts data
  const [alerts, setAlerts] = useState(null);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  
  // Risks data
  const [risks, setRisks] = useState([]);
  const [risksTotal, setRisksTotal] = useState(0);
  const [riskFilter, setRiskFilter] = useState({ status: '', risk_level: '' });
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  
  // Incidents data
  const [incidents, setIncidents] = useState([]);
  const [incidentsTotal, setIncidentsTotal] = useState(0);
  const [incidentFilter, setIncidentFilter] = useState({ status: '', severity: '' });
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  
  // Assets data
  const [assets, setAssets] = useState([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetFilter, setAssetFilter] = useState({ asset_type: '', criticality: '' });
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  
  // Compliance data
  const [compliance, setCompliance] = useState([]);
  const [complianceDashboard, setComplianceDashboard] = useState(null);

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (token) {
      fetchDashboard();
      fetchAlerts();
    }
  }, [token]);

  useEffect(() => {
    if (token && activeTab === 'risks') fetchRisks();
    if (token && activeTab === 'incidents') fetchIncidents();
    if (token && activeTab === 'assets') fetchAssets();
    if (token && activeTab === 'compliance') {
      fetchCompliance();
      fetchComplianceDashboard();
    }
  }, [token, activeTab, riskFilter, incidentFilter, assetFilter]);

  // API calls
  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/isms/alerts`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data);
      }
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/isms/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRisks = async () => {
    try {
      const params = new URLSearchParams();
      if (riskFilter.status) params.append('status', riskFilter.status);
      if (riskFilter.risk_level) params.append('risk_level', riskFilter.risk_level);
      
      const res = await fetch(`${API_URL}/api/isms/risks?${params}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setRisks(data.risks);
        setRisksTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch risks:', error);
    }
  };

  const fetchIncidents = async () => {
    try {
      const params = new URLSearchParams();
      if (incidentFilter.status) params.append('status', incidentFilter.status);
      if (incidentFilter.severity) params.append('severity', incidentFilter.severity);
      
      const res = await fetch(`${API_URL}/api/isms/incidents?${params}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setIncidents(data.incidents);
        setIncidentsTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const params = new URLSearchParams();
      if (assetFilter.asset_type) params.append('asset_type', assetFilter.asset_type);
      if (assetFilter.criticality) params.append('criticality', assetFilter.criticality);
      
      const res = await fetch(`${API_URL}/api/isms/assets?${params}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAssets(data.assets);
        setAssetsTotal(data.total);
      }
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const fetchCompliance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/isms/compliance`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCompliance(data);
      }
    } catch (error) {
      console.error('Failed to fetch compliance:', error);
    }
  };

  const fetchComplianceDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/isms/compliance/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setComplianceDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch compliance dashboard:', error);
    }
  };

  // CRUD operations
  const saveRisk = async (riskData) => {
    try {
      const url = editingRisk 
        ? `${API_URL}/api/isms/risks/${editingRisk.id}`
        : `${API_URL}/api/isms/risks`;
      
      const res = await fetch(url, {
        method: editingRisk ? 'PUT' : 'POST',
        headers: apiHeaders,
        body: JSON.stringify(riskData)
      });

      if (res.ok) {
        toast.success(editingRisk ? 'Risk updated' : 'Risk created');
        setShowRiskDialog(false);
        setEditingRisk(null);
        fetchRisks();
        fetchDashboard();
      } else {
        throw new Error('Failed to save risk');
      }
    } catch (error) {
      toast.error('Failed to save risk');
    }
  };

  const deleteRisk = async (riskId) => {
    if (!confirm('Are you sure you want to delete this risk?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/isms/risks/${riskId}`, {
        method: 'DELETE',
        headers: apiHeaders
      });

      if (res.ok) {
        toast.success('Risk deleted');
        fetchRisks();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to delete risk');
    }
  };

  const saveIncident = async (incidentData) => {
    try {
      const url = editingIncident 
        ? `${API_URL}/api/isms/incidents/${editingIncident.id}`
        : `${API_URL}/api/isms/incidents`;
      
      const res = await fetch(url, {
        method: editingIncident ? 'PUT' : 'POST',
        headers: apiHeaders,
        body: JSON.stringify(incidentData)
      });

      if (res.ok) {
        toast.success(editingIncident ? 'Incident updated' : 'Incident reported');
        setShowIncidentDialog(false);
        setEditingIncident(null);
        fetchIncidents();
        fetchDashboard();
      } else {
        throw new Error('Failed to save incident');
      }
    } catch (error) {
      toast.error('Failed to save incident');
    }
  };

  const saveAsset = async (assetData) => {
    try {
      const url = editingAsset 
        ? `${API_URL}/api/isms/assets/${editingAsset.id}`
        : `${API_URL}/api/isms/assets`;
      
      const res = await fetch(url, {
        method: editingAsset ? 'PUT' : 'POST',
        headers: apiHeaders,
        body: JSON.stringify(assetData)
      });

      if (res.ok) {
        toast.success(editingAsset ? 'Asset updated' : 'Asset created');
        setShowAssetDialog(false);
        setEditingAsset(null);
        fetchAssets();
        fetchDashboard();
      } else {
        throw new Error('Failed to save asset');
      }
    } catch (error) {
      toast.error('Failed to save asset');
    }
  };

  const updateComplianceStatus = async (reqId, status, psoeLevel) => {
    try {
      const res = await fetch(`${API_URL}/api/isms/compliance/${reqId}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status, psoe_level: psoeLevel })
      });

      if (res.ok) {
        toast.success('Compliance status updated');
        fetchCompliance();
        fetchComplianceDashboard();
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Failed to update compliance');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="isms-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-indigo-500" />
            {t('isms.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('isms.subtitle')}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Alerts Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAlertsPanel(!showAlertsPanel)}
            className={cn(
              "relative",
              alerts?.critical_count > 0 && "border-red-500 text-red-600 hover:bg-red-50"
            )}
            data-testid="alerts-button"
          >
            {alerts?.critical_count > 0 ? (
              <BellRing className="w-4 h-4 mr-2 animate-pulse" />
            ) : (
              <Bell className="w-4 h-4 mr-2" />
            )}
            {t('frms.alerts')}
            {alerts?.total_alerts > 0 && (
              <Badge className={cn(
                "ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs",
                alerts?.critical_count > 0 ? "bg-red-500" : "bg-orange-500"
              )}>
                {alerts?.total_alerts}
              </Badge>
            )}
          </Button>

          {dashboard && (
            <>
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400">Days to Deadline</p>
                <p className={cn(
                  "text-lg font-bold",
                  dashboard.days_to_deadline < 90 ? "text-red-500" : 
                  dashboard.days_to_deadline < 180 ? "text-orange-500" : "text-green-500"
                )}>
                  {dashboard.days_to_deadline}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500 dark:text-gray-400">PSOE Level</p>
                <Badge className={cn(PSOE_LEVELS[dashboard.psoe_level]?.color, "text-white")}>
                  {PSOE_LEVELS[dashboard.psoe_level]?.label || 'Unknown'}
                </Badge>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Alerts Panel */}
      {showAlertsPanel && alerts && (
        <Card className="border-l-4 border-l-indigo-500 bg-gradient-to-r from-indigo-500/5 to-transparent">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BellRing className="w-5 h-5 text-indigo-500" />
                Compliance Alerts
                <Badge variant="outline" className="ml-2">
                  {alerts.days_to_deadline} days to deadline
                </Badge>
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAlertsPanel(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>
              EASA Part-IS compliance deadline: October 16, 2025 | Current score: {alerts.compliance_score}%
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.alerts?.length > 0 ? (
              <div className="space-y-3">
                {alerts.alerts.map((alert, idx) => {
                  const alertStyle = ALERT_COLORS[alert.type] || ALERT_COLORS.info;
                  const AlertIcon = alertStyle.icon;
                  return (
                    <div
                      key={idx}
                      className={cn(
                        "p-4 rounded-lg border-l-4 flex items-start gap-3",
                        alertStyle.bg,
                        alertStyle.border
                      )}
                    >
                      <AlertIcon className={cn("w-5 h-5 mt-0.5 flex-shrink-0", alertStyle.text)} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={cn("font-semibold text-sm", alertStyle.text)}>
                            {alert.title}
                          </span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {alert.category}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300">{alert.message}</p>
                        {alert.action_url && (
                          <Button
                            variant="link"
                            size="sm"
                            className={cn("p-0 h-auto mt-2", alertStyle.text)}
                            onClick={() => {
                              setActiveTab(alert.action_url.includes('risks') ? 'risks' : 
                                          alert.action_url.includes('incidents') ? 'incidents' : 
                                          alert.action_url.includes('compliance') ? 'compliance' : 'dashboard');
                            }}
                          >
                            View Details <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6">
                <ShieldCheck className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className="text-green-600 font-medium">No compliance alerts</p>
                <p className="text-sm text-gray-500">Your ISMS compliance is on track</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <Activity className="w-4 h-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="risks" data-testid="tab-risks">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Risks
          </TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">
            <ShieldAlert className="w-4 h-4 mr-2" />
            Incidents
          </TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets">
            <Server className="w-4 h-4 mr-2" />
            Assets
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            <CheckCircle className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {dashboard && (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-200 dark:border-red-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Critical Risks</p>
                        <p className="text-2xl font-bold text-red-600">{dashboard.critical_risks}</p>
                      </div>
                      <AlertCircle className="w-8 h-8 text-red-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-200 dark:border-orange-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">High Risks</p>
                        <p className="text-2xl font-bold text-orange-600">{dashboard.high_risks}</p>
                      </div>
                      <AlertTriangle className="w-8 h-8 text-orange-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-200 dark:border-yellow-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Open Incidents</p>
                        <p className="text-2xl font-bold text-yellow-600">{dashboard.open_incidents}</p>
                      </div>
                      <ShieldAlert className="w-8 h-8 text-yellow-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-200 dark:border-blue-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total Assets</p>
                        <p className="text-2xl font-bold text-blue-600">{dashboard.total_assets}</p>
                      </div>
                      <Server className="w-8 h-8 text-blue-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200 dark:border-purple-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Vulnerabilities</p>
                        <p className="text-2xl font-bold text-purple-600">{dashboard.vulnerabilities_open}</p>
                      </div>
                      <FileWarning className="w-8 h-8 text-purple-500/50" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200 dark:border-green-900">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Compliance</p>
                        <p className="text-2xl font-bold text-green-600">{dashboard.compliance_score}%</p>
                      </div>
                      <ShieldCheck className="w-8 h-8 text-green-500/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Activity */}
              <div className="grid md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <ShieldAlert className="w-5 h-5 text-red-500" />
                      Recent Incidents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.recent_incidents?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.recent_incidents.map(incident => (
                          <div key={incident.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={cn("w-2 h-2 rounded-full", SEVERITY_COLORS[incident.severity])} />
                              <div>
                                <p className="font-medium text-sm">{incident.title}</p>
                                <p className="text-xs text-gray-500">{new Date(incident.created_at).toLocaleDateString()}</p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs">{incident.status}</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No recent incidents</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-indigo-500" />
                      Upcoming Risk Reviews
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dashboard.upcoming_reviews?.length > 0 ? (
                      <div className="space-y-3">
                        {dashboard.upcoming_reviews.map(review => (
                          <div key={review.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={cn("w-2 h-2 rounded-full", RISK_LEVELS[review.risk_level]?.color)} />
                              <div>
                                <p className="font-medium text-sm">{review.title}</p>
                                <p className="text-xs text-gray-500">Review: {review.review_date}</p>
                              </div>
                            </div>
                            <Badge className={cn(RISK_LEVELS[review.risk_level]?.color, "text-white text-xs")}>
                              {review.risk_level}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">No upcoming reviews</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Select value={riskFilter.risk_level || "all"} onValueChange={(v) => setRiskFilter(f => ({...f, risk_level: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Risk Level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={riskFilter.status || "all"} onValueChange={(v) => setRiskFilter(f => ({...f, status: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="identified">Identified</SelectItem>
                  <SelectItem value="assessed">Assessed</SelectItem>
                  <SelectItem value="treating">Treating</SelectItem>
                  <SelectItem value="monitoring">Monitoring</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setEditingRisk(null); setShowRiskDialog(true); }} data-testid="add-risk-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Risk
            </Button>
          </div>

          <div className="grid gap-4">
            {risks.map(risk => (
              <Card key={risk.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={cn(RISK_LEVELS[risk.risk_level]?.color, "text-white")}>
                          {risk.risk_level?.toUpperCase()}
                        </Badge>
                        <Badge variant="outline">{risk.status}</Badge>
                        <Badge variant="outline">{risk.treatment_strategy}</Badge>
                      </div>
                      <h3 className="font-semibold text-lg">{risk.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{risk.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span>Threat: {risk.threat_source}</span>
                        <span>Category: {risk.category}</span>
                        {risk.owner_name && <span>Owner: {risk.owner_name}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingRisk(risk); setShowRiskDialog(true); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteRisk(risk.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            {risks.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No security risks found</p>
                  <Button className="mt-4" onClick={() => setShowRiskDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Risk
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Incidents Tab */}
        <TabsContent value="incidents" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Select value={incidentFilter.severity || "all"} onValueChange={(v) => setIncidentFilter(f => ({...f, severity: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Severity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Severity</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
              <Select value={incidentFilter.status || "all"} onValueChange={(v) => setIncidentFilter(f => ({...f, status: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="reported">Reported</SelectItem>
                  <SelectItem value="investigating">Investigating</SelectItem>
                  <SelectItem value="contained">Contained</SelectItem>
                  <SelectItem value="recovering">Recovering</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setEditingIncident(null); setShowIncidentDialog(true); }} className="bg-red-600 hover:bg-red-700" data-testid="report-incident-btn">
              <Plus className="w-4 h-4 mr-2" />
              Report Incident
            </Button>
          </div>

          <div className="grid gap-4">
            {incidents.map(incident => (
              <Card key={incident.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={cn(SEVERITY_COLORS[incident.severity], "text-white")}>
                          {incident.severity?.toUpperCase()}
                        </Badge>
                        <Badge variant="outline">{incident.status}</Badge>
                        <Badge variant="outline">{incident.category}</Badge>
                      </div>
                      <h3 className="font-semibold text-lg">{incident.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{incident.description}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span>Detected: {new Date(incident.detection_time).toLocaleString()}</span>
                        <span>Method: {incident.detection_method}</span>
                        {incident.assigned_to_name && <span>Assigned: {incident.assigned_to_name}</span>}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingIncident(incident); setShowIncidentDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {incidents.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <ShieldCheck className="w-12 h-12 mx-auto text-green-300 mb-4" />
                  <p className="text-gray-500">No security incidents reported</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Assets Tab */}
        <TabsContent value="assets" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2">
              <Select value={assetFilter.asset_type || "all"} onValueChange={(v) => setAssetFilter(f => ({...f, asset_type: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Asset Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="data">Data</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
              <Select value={assetFilter.criticality || "all"} onValueChange={(v) => setAssetFilter(f => ({...f, criticality: v === "all" ? "" : v}))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Criticality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Criticality</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => { setEditingAsset(null); setShowAssetDialog(true); }} data-testid="add-asset-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Asset
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => {
              const AssetIcon = ASSET_ICONS[asset.asset_type] || Server;
              return (
                <Card key={asset.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className={cn(
                        "p-2 rounded-lg",
                        asset.criticality === 'critical' ? "bg-red-100 dark:bg-red-900/30" :
                        asset.criticality === 'important' ? "bg-orange-100 dark:bg-orange-900/30" :
                        "bg-blue-100 dark:bg-blue-900/30"
                      )}>
                        <AssetIcon className={cn(
                          "w-5 h-5",
                          asset.criticality === 'critical' ? "text-red-600" :
                          asset.criticality === 'important' ? "text-orange-600" :
                          "text-blue-600"
                        )} />
                      </div>
                      <Badge variant="outline" className="text-xs">{asset.criticality}</Badge>
                    </div>
                    <h3 className="font-semibold">{asset.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{asset.description}</p>
                    <div className="flex items-center justify-between mt-4 text-xs text-gray-500">
                      <span className="capitalize">{asset.asset_type}</span>
                      {asset.risk_count > 0 && (
                        <Badge variant="outline" className="text-orange-600">
                          {asset.risk_count} risks
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                      <Button variant="ghost" size="sm" className="flex-1" onClick={() => { setEditingAsset(asset); setShowAssetDialog(true); }}>
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {assets.length === 0 && (
              <Card className="md:col-span-2 lg:col-span-3">
                <CardContent className="p-8 text-center">
                  <Server className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No assets registered</p>
                  <Button className="mt-4" onClick={() => setShowAssetDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Asset
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          {complianceDashboard && (
            <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-200 dark:border-indigo-900">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-4 gap-6">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Overall PSOE Level</p>
                    <Badge className={cn(PSOE_LEVELS[complianceDashboard.overall_psoe_level]?.color, "text-white mt-2")}>
                      {PSOE_LEVELS[complianceDashboard.overall_psoe_level]?.label}
                    </Badge>
                    <Progress 
                      value={PSOE_LEVELS[complianceDashboard.overall_psoe_level]?.value || 0} 
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Compliance Score</p>
                    <p className="text-3xl font-bold text-indigo-600">{complianceDashboard.compliance_percentage}%</p>
                    <p className="text-xs text-gray-500">{complianceDashboard.compliant_count} of {complianceDashboard.total_requirements} compliant</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Days to Deadline</p>
                    <p className={cn(
                      "text-3xl font-bold",
                      complianceDashboard.days_to_deadline < 90 ? "text-red-600" :
                      complianceDashboard.days_to_deadline < 180 ? "text-orange-600" : "text-green-600"
                    )}>
                      {complianceDashboard.days_to_deadline}
                    </p>
                    <p className="text-xs text-gray-500">October 16, 2025</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Status Breakdown</p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge className="bg-green-500 text-white">{complianceDashboard.compliant_count} Compliant</Badge>
                      <Badge className="bg-yellow-500 text-white">{complianceDashboard.in_progress_count} In Progress</Badge>
                      <Badge className="bg-gray-400 text-white">{complianceDashboard.not_started_count} Not Started</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {compliance.map(req => (
              <Card key={req.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="font-mono text-xs">{req.requirement_id}</Badge>
                        <Badge className={cn(
                          req.status === 'compliant' ? 'bg-green-500' :
                          req.status === 'in_progress' ? 'bg-yellow-500' :
                          req.status === 'non_compliant' ? 'bg-red-500' : 'bg-gray-400',
                          "text-white text-xs"
                        )}>
                          {req.status?.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className="text-xs">{req.category}</Badge>
                      </div>
                      <h3 className="font-semibold">{req.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{req.description}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={req.status} 
                        onValueChange={(v) => updateComplianceStatus(req.id, v, req.psoe_level)}
                      >
                        <SelectTrigger className="w-[130px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="in_progress">In Progress</SelectItem>
                          <SelectItem value="compliant">Compliant</SelectItem>
                          <SelectItem value="non_compliant">Non-Compliant</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select 
                        value={req.psoe_level} 
                        onValueChange={(v) => updateComplianceStatus(req.id, req.status, v)}
                      >
                        <SelectTrigger className="w-[120px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="not_started">Not Started</SelectItem>
                          <SelectItem value="present">Present</SelectItem>
                          <SelectItem value="suitable">Suitable</SelectItem>
                          <SelectItem value="operational">Operational</SelectItem>
                          <SelectItem value="effective">Effective</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Risk Dialog */}
      <RiskDialog 
        open={showRiskDialog} 
        onOpenChange={setShowRiskDialog}
        risk={editingRisk}
        onSave={saveRisk}
        assets={assets}
      />

      {/* Incident Dialog */}
      <IncidentDialog 
        open={showIncidentDialog} 
        onOpenChange={setShowIncidentDialog}
        incident={editingIncident}
        onSave={saveIncident}
      />

      {/* Asset Dialog */}
      <AssetDialog 
        open={showAssetDialog} 
        onOpenChange={setShowAssetDialog}
        asset={editingAsset}
        onSave={saveAsset}
      />
    </div>
  );
}

// Risk Dialog Component
function RiskDialog({ open, onOpenChange, risk, onSave, assets }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'technical',
    threat_source: '',
    vulnerability: '',
    affected_assets: [],
    likelihood: 'possible',
    impact: 'moderate',
    treatment_strategy: 'mitigate',
    treatment_plan: ''
  });

  useEffect(() => {
    if (risk) {
      setFormData({
        title: risk.title || '',
        description: risk.description || '',
        category: risk.category || 'technical',
        threat_source: risk.threat_source || '',
        vulnerability: risk.vulnerability || '',
        affected_assets: risk.affected_assets || [],
        likelihood: risk.likelihood || 'possible',
        impact: risk.impact || 'moderate',
        treatment_strategy: risk.treatment_strategy || 'mitigate',
        treatment_plan: risk.treatment_plan || '',
        status: risk.status || 'identified'
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'technical',
        threat_source: '',
        vulnerability: '',
        affected_assets: [],
        likelihood: 'possible',
        impact: 'moderate',
        treatment_strategy: 'mitigate',
        treatment_plan: ''
      });
    }
  }, [risk, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{risk ? 'Edit Security Risk' : 'Add Security Risk'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData(f => ({...f, title: e.target.value}))}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData(f => ({...f, description: e.target.value}))}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(f => ({...f, category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technical">Technical</SelectItem>
                  <SelectItem value="operational">Operational</SelectItem>
                  <SelectItem value="organizational">Organizational</SelectItem>
                  <SelectItem value="external">External</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Threat Source</Label>
              <Input 
                value={formData.threat_source} 
                onChange={(e) => setFormData(f => ({...f, threat_source: e.target.value}))}
                placeholder="e.g., External attacker, Malware"
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Vulnerability</Label>
              <Input 
                value={formData.vulnerability} 
                onChange={(e) => setFormData(f => ({...f, vulnerability: e.target.value}))}
                placeholder="e.g., Unpatched software, Weak authentication"
                required
              />
            </div>
            <div>
              <Label>Likelihood</Label>
              <Select value={formData.likelihood} onValueChange={(v) => setFormData(f => ({...f, likelihood: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rare">Rare</SelectItem>
                  <SelectItem value="unlikely">Unlikely</SelectItem>
                  <SelectItem value="possible">Possible</SelectItem>
                  <SelectItem value="likely">Likely</SelectItem>
                  <SelectItem value="almost_certain">Almost Certain</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Impact</Label>
              <Select value={formData.impact} onValueChange={(v) => setFormData(f => ({...f, impact: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="negligible">Negligible</SelectItem>
                  <SelectItem value="minor">Minor</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="major">Major</SelectItem>
                  <SelectItem value="catastrophic">Catastrophic</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Treatment Strategy</Label>
              <Select value={formData.treatment_strategy} onValueChange={(v) => setFormData(f => ({...f, treatment_strategy: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="accept">Accept</SelectItem>
                  <SelectItem value="mitigate">Mitigate</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                  <SelectItem value="avoid">Avoid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {risk && (
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(f => ({...f, status: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="identified">Identified</SelectItem>
                    <SelectItem value="assessed">Assessed</SelectItem>
                    <SelectItem value="treating">Treating</SelectItem>
                    <SelectItem value="monitoring">Monitoring</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="md:col-span-2">
              <Label>Treatment Plan</Label>
              <Textarea 
                value={formData.treatment_plan} 
                onChange={(e) => setFormData(f => ({...f, treatment_plan: e.target.value}))}
                rows={3}
                placeholder="Describe the risk treatment approach..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">{risk ? 'Update' : 'Create'} Risk</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Incident Dialog Component
function IncidentDialog({ open, onOpenChange, incident, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    severity: 'medium',
    affected_systems: [],
    affected_data: '',
    detection_method: '',
    detection_time: new Date().toISOString().slice(0, 16),
    initial_response: ''
  });

  useEffect(() => {
    if (incident) {
      setFormData({
        title: incident.title || '',
        description: incident.description || '',
        category: incident.category || 'other',
        severity: incident.severity || 'medium',
        status: incident.status || 'reported',
        affected_systems: incident.affected_systems || [],
        affected_data: incident.affected_data || '',
        detection_method: incident.detection_method || '',
        detection_time: incident.detection_time?.slice(0, 16) || new Date().toISOString().slice(0, 16),
        initial_response: incident.initial_response || '',
        containment_actions: incident.containment_actions || '',
        root_cause: incident.root_cause || '',
        lessons_learned: incident.lessons_learned || ''
      });
    } else {
      setFormData({
        title: '',
        description: '',
        category: 'other',
        severity: 'medium',
        affected_systems: [],
        affected_data: '',
        detection_method: '',
        detection_time: new Date().toISOString().slice(0, 16),
        initial_response: ''
      });
    }
  }, [incident, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{incident ? 'Update Security Incident' : 'Report Security Incident'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Title</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData(f => ({...f, title: e.target.value}))}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData(f => ({...f, description: e.target.value}))}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(f => ({...f, category: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="malware">Malware</SelectItem>
                  <SelectItem value="phishing">Phishing</SelectItem>
                  <SelectItem value="unauthorized_access">Unauthorized Access</SelectItem>
                  <SelectItem value="data_breach">Data Breach</SelectItem>
                  <SelectItem value="dos_attack">DoS Attack</SelectItem>
                  <SelectItem value="insider_threat">Insider Threat</SelectItem>
                  <SelectItem value="system_failure">System Failure</SelectItem>
                  <SelectItem value="policy_violation">Policy Violation</SelectItem>
                  <SelectItem value="physical_security">Physical Security</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Severity</Label>
              <Select value={formData.severity} onValueChange={(v) => setFormData(f => ({...f, severity: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {incident && (
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(f => ({...f, status: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="reported">Reported</SelectItem>
                    <SelectItem value="investigating">Investigating</SelectItem>
                    <SelectItem value="contained">Contained</SelectItem>
                    <SelectItem value="recovering">Recovering</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Detection Method</Label>
              <Input 
                value={formData.detection_method} 
                onChange={(e) => setFormData(f => ({...f, detection_method: e.target.value}))}
                placeholder="e.g., SIEM alert, User report"
                required
              />
            </div>
            <div>
              <Label>Detection Time</Label>
              <Input 
                type="datetime-local"
                value={formData.detection_time} 
                onChange={(e) => setFormData(f => ({...f, detection_time: e.target.value}))}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Initial Response</Label>
              <Textarea 
                value={formData.initial_response} 
                onChange={(e) => setFormData(f => ({...f, initial_response: e.target.value}))}
                rows={2}
                placeholder="Describe initial response actions taken..."
              />
            </div>
            {incident && (
              <>
                <div className="md:col-span-2">
                  <Label>Containment Actions</Label>
                  <Textarea 
                    value={formData.containment_actions} 
                    onChange={(e) => setFormData(f => ({...f, containment_actions: e.target.value}))}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Root Cause</Label>
                  <Textarea 
                    value={formData.root_cause} 
                    onChange={(e) => setFormData(f => ({...f, root_cause: e.target.value}))}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Lessons Learned</Label>
                  <Textarea 
                    value={formData.lessons_learned} 
                    onChange={(e) => setFormData(f => ({...f, lessons_learned: e.target.value}))}
                    rows={2}
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className={incident ? "" : "bg-red-600 hover:bg-red-700"}>
              {incident ? 'Update' : 'Report'} Incident
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Asset Dialog Component
function AssetDialog({ open, onOpenChange, asset, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    asset_type: 'software',
    criticality: 'standard',
    location: '',
    vendor: '',
    version: '',
    ip_address: '',
    data_classification: ''
  });

  useEffect(() => {
    if (asset) {
      setFormData({
        name: asset.name || '',
        description: asset.description || '',
        asset_type: asset.asset_type || 'software',
        criticality: asset.criticality || 'standard',
        status: asset.status || 'active',
        location: asset.location || '',
        vendor: asset.vendor || '',
        version: asset.version || '',
        ip_address: asset.ip_address || '',
        data_classification: asset.data_classification || ''
      });
    } else {
      setFormData({
        name: '',
        description: '',
        asset_type: 'software',
        criticality: 'standard',
        location: '',
        vendor: '',
        version: '',
        ip_address: '',
        data_classification: ''
      });
    }
  }, [asset, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{asset ? 'Edit Asset' : 'Add Asset'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Name</Label>
              <Input 
                value={formData.name} 
                onChange={(e) => setFormData(f => ({...f, name: e.target.value}))}
                required
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description} 
                onChange={(e) => setFormData(f => ({...f, description: e.target.value}))}
                rows={2}
                required
              />
            </div>
            <div>
              <Label>Asset Type</Label>
              <Select value={formData.asset_type} onValueChange={(v) => setFormData(f => ({...f, asset_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hardware">Hardware</SelectItem>
                  <SelectItem value="software">Software</SelectItem>
                  <SelectItem value="data">Data</SelectItem>
                  <SelectItem value="network">Network</SelectItem>
                  <SelectItem value="personnel">Personnel</SelectItem>
                  <SelectItem value="facility">Facility</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Criticality</Label>
              <Select value={formData.criticality} onValueChange={(v) => setFormData(f => ({...f, criticality: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="important">Important</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Input 
                value={formData.location} 
                onChange={(e) => setFormData(f => ({...f, location: e.target.value}))}
                placeholder="e.g., Data Center A"
              />
            </div>
            <div>
              <Label>Vendor</Label>
              <Input 
                value={formData.vendor} 
                onChange={(e) => setFormData(f => ({...f, vendor: e.target.value}))}
              />
            </div>
            <div>
              <Label>Version</Label>
              <Input 
                value={formData.version} 
                onChange={(e) => setFormData(f => ({...f, version: e.target.value}))}
              />
            </div>
            <div>
              <Label>IP Address</Label>
              <Input 
                value={formData.ip_address} 
                onChange={(e) => setFormData(f => ({...f, ip_address: e.target.value}))}
                placeholder="e.g., 192.168.1.100"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Data Classification</Label>
              <Select value={formData.data_classification || ''} onValueChange={(v) => setFormData(f => ({...f, data_classification: v}))}>
                <SelectTrigger><SelectValue placeholder="Select classification" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="confidential">Confidential</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit">{asset ? 'Update' : 'Create'} Asset</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
